import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Translucent dark fill for settings text inputs / selects / textareas — lets the
 * page + card tint show through instead of a flat opaque field (OG used
 * rgba(0,0,0,0.25), darkening to 0.4 on focus). Pass to a field to override its
 * `bg-input`.
 */
export const TRANSLUCENT_FIELD = 'bg-black/25 focus:bg-black/40';

/* Shared layout atoms for the settings tabs — the OG `.v16-card`, `.v16-row`,
   and `.v16-field` shells, re-expressed as small composable components. */

interface SettingsCardProps {
  /** Omit to render a header-less card that hugs its rows. */
  title?: string;
  desc?: string;
  /** Optional control rendered top-right of the header (e.g. "+ Create"). */
  action?: ReactNode;
  /** Red title treatment for the Danger zone (OG inline `color:#ff5b67`). */
  danger?: boolean;
  children?: ReactNode;
  className?: string;
}

/** A titled settings panel (OG `.v16-card`). */
export function SettingsCard({ title, desc, action, danger, children, className }: SettingsCardProps) {
  const hasHeader = Boolean(title || desc || action);
  return (
    <section className={cn('mb-[18px] rounded-2xl border border-edge bg-white/[0.03] p-6', className)}>
      {hasHeader && (
        <header className="mb-[18px] flex items-start justify-between gap-4">
          <div>
            {title && (
              <h3 className={cn('text-base font-bold', danger ? 'text-danger' : 'text-hi')}>{title}</h3>
            )}
            {desc && <p className="mt-0.5 text-xs text-mid">{desc}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

interface SettingsRowProps {
  title: string;
  desc?: ReactNode;
  /** The control on the right (switch / button / badge). */
  control?: ReactNode;
  /** Top-align when the control is tall (OG `align-items:flex-start`). */
  alignStart?: boolean;
}

/** A label/description + right-aligned control, divider-separated (OG `.v16-row`). */
export function SettingsRow({ title, desc, control, alignStart }: SettingsRowProps) {
  return (
    <div
      className={cn(
        'flex justify-between gap-4 border-b border-edge py-3.5 last:border-b-0',
        alignStart ? 'items-start' : 'items-center',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-hi">{title}</div>
        {desc && <div className="mt-0.5 text-xs leading-snug text-mid">{desc}</div>}
      </div>
      {control}
    </div>
  );
}

interface FieldProps {
  label?: string;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  className?: string;
}

/** A labelled form field with an optional hint or error line (OG `.v16-field`). */
export function Field({ label, hint, error, children, className }: FieldProps) {
  return (
    <div className={cn('mb-3.5 flex flex-col gap-1.5 last:mb-0', className)}>
      {label && (
        <label className="text-[11px] font-bold uppercase tracking-[0.4px] text-mid">{label}</label>
      )}
      {children}
      {error ? (
        <p className="text-xs leading-snug text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[11px] leading-snug text-lo">{hint}</p>
      ) : null}
    </div>
  );
}
